import { styled } from '@mui/material';
import {
  Grid,
  Button,
  Checkbox,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import { LicenseForm } from './types';
import { ChangeEvent } from 'react';
import { ModuleType } from '../analyzerModules/types';

const StyledGrid = styled(Grid)(
  ({ theme }) => `
  '& > *': {
    margin: ${theme.spacing(1)},
  },
`
);

interface Props {
  handleUpdateLicense: (license: LicenseForm) => void;
  handleCreateNewLicense: () => void;
  handleCreateLicense: () => Promise<void>;
  handleImportLicense: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
  licenseAmount: number;
  licenses: LicenseForm[];
  selectedLicenseIndex: number;
  isDemoLicense: boolean;
  handleDemoLicenseChecked: (e: ChangeEvent<HTMLInputElement>) => void;
}

const CreateLicensesForm = ({
  handleUpdateLicense,
  handleCreateNewLicense,
  handleCreateLicense,
  handleImportLicense,
  licenseAmount,
  licenses,
  selectedLicenseIndex,
  isDemoLicense,
  handleDemoLicenseChecked,
}: Props) => {
  const selectedLicense = licenses[selectedLicenseIndex];

  const handleChanged = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    handleUpdateLicense({
      ...selectedLicense,
      serial: value,
    });
  };

  const handleChecked = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    if (checked) {
      const newModules = [...selectedLicense.modules, name];
      handleUpdateLicense({
        ...selectedLicense,
        modules: newModules,
      });
    } else {
      const filteredModules = selectedLicense.modules.filter(
        (module) => module !== name
      );
      handleUpdateLicense({
        ...selectedLicense,
        modules: filteredModules,
      });
    }
  };

  const checkIfModuleSelected = (moduleType: ModuleType) => {
    return selectedLicense?.modules?.includes(moduleType);
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDemoLicense}
                  onChange={handleDemoLicenseChecked}
                  disabled={!selectedLicense}
                />
              }
              label='Create as Demo License'
            />
          </FormGroup>
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            id='serial'
            name='serial'
            label='Analyzer Serial'
            fullWidth
            onChange={handleChanged}
            value={selectedLicense?.serial || ''}
            disabled={!selectedLicense}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl component='fieldset'>
            <FormLabel component='legend'>Modules</FormLabel>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkIfModuleSelected(ModuleType.LITE) || false}
                    onChange={handleChecked}
                    name='LITE'
                    disabled={!selectedLicense}
                  />
                }
                label='LITE'
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkIfModuleSelected(ModuleType.BASE) || false}
                    onChange={handleChecked}
                    name='BASE'
                    disabled={!selectedLicense}
                  />
                }
                label='BASE'
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkIfModuleSelected(ModuleType.GEO) || false}
                    onChange={handleChecked}
                    name='GEO'
                    disabled={!selectedLicense}
                  />
                }
                label='GEO'
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkIfModuleSelected(ModuleType.LEO) || false}
                    onChange={handleChecked}
                    name='LEO'
                    disabled={!selectedLicense}
                  />
                }
                label='LEO'
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      checkIfModuleSelected(ModuleType.TIMESHARE) || false
                    }
                    onChange={handleChecked}
                    name='TIMESHARE'
                    disabled={!selectedLicense}
                  />
                }
                label='TIMESHARE'
              />
            </FormGroup>
          </FormControl>
        </Grid>
        <StyledGrid item>
          <Button
            variant='contained'
            color='primary'
            component='span'
            onClick={handleCreateNewLicense}
          >
            New
          </Button>

          <input
            style={{ display: 'none' }}
            id='contained-button-file'
            type='file'
            accept='.lic'
            onChange={handleImportLicense}
          />
          <label htmlFor='contained-button-file'>
            <Button variant='contained' color='primary' component='span'>
              Upload License
            </Button>
          </label>
          <Button
            variant='contained'
            color='primary'
            component='span'
            onClick={handleCreateLicense}
            disabled={licenseAmount === 0}
          >
            Create License
          </Button>
        </StyledGrid>
      </Grid>
    </>
  );
};

export default CreateLicensesForm;
